import { Button as BaseButton } from "@base-ui/react/button";
import classNames from "classnames";
import * as React from "react";

import variants from "./buttonVariants.module.css";
import styles from "./RobotoButton.module.css";

/**
 * Visual style of the button. `primary` is the solid brand call-to-action;
 * `secondary` is the neutral outlined control that sits beside it.
 */
export type RobotoButtonVariant = "primary" | "secondary";

export interface RobotoButtonProps extends React.AriaAttributes {
  /** Visual style. */
  variant?: RobotoButtonVariant;
  /**
   * Icon rendered before the children. Accepts any React element (e.g. a
   * lucide or MUI icon). The variant sizes and colors it to match the label.
   */
  icon?: React.ReactNode;
  /** Shows a spinner after the label and disables interaction. */
  loading?: boolean;
  children?: React.ReactNode;
  /** Extra class(es) for call-site layout/positioning. */
  className?: string;
  disabled?: boolean;
  /**
   * @default "button"
   */
  type?: "button" | "submit" | "reset";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  id?: string;
  title?: string;
}

const variantClass: Record<RobotoButtonVariant, string> = {
  primary: variants.primary,
  secondary: variants.secondary,
};

/**
 * A standard Button component.
 *
 * @todo Rename to Button in a follow up PR and deprecate old Button
 * component as part of moving off of MUI.
 *
 * @example
 * ```tsx
 * <RobotoButton variant="primary" icon={<PlusIcon />} onClick={handleClick}>
 *   New
 * </RobotoButton>
 * ```
 */
export const RobotoButton = React.forwardRef<
  HTMLButtonElement,
  RobotoButtonProps
>(function RobotoButton(
  {
    variant = "primary",
    icon,
    loading = false,
    disabled,
    type = "button",
    className,
    children,
    ...props
  },
  ref,
) {
  const { "aria-label": ariaLabel, ...rest } = props;
  const ariaLabelledBy = rest["aria-labelledby"];

  // Derive the accessible name from a string label so it survives the visible
  // label being hidden — e.g. when a toolbar collapses its buttons to
  // icon-only on small screens (see `.label` in buttonVariants.module.css).
  // Skip derivation when the caller already named the button (aria-label /
  // aria-labelledby), and only for plain-string children (JSX labels can't be
  // read out here — those callers pass their own aria-label).
  const labelText =
    ariaLabel === undefined &&
    ariaLabelledBy === undefined &&
    typeof children === "string"
      ? children
      : undefined;

  const hasLabel = children !== undefined && children !== null;

  return (
    <BaseButton
      ref={ref}
      className={classNames(variantClass[variant], className)}
      disabled={disabled || loading}
      type={type}
      data-loading={loading || undefined}
      aria-label={ariaLabel ?? labelText}
      {...rest}
    >
      {!loading && icon}
      {hasLabel && <span className={variants.label}>{children}</span>}
      {loading && <span className={styles.spinner} aria-hidden="true" />}
    </BaseButton>
  );
});
